import { Tooltip } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "../../components/button";
import CustomTabs from "../../components/CustomTabs";
import DeleteModal from "../../components/modals/DeleteModal";
import TripCompleteModal from "../../components/modals/TripCompleteModal";
import Search from "../../components/Search";
import UserAvatar from "../../components/UserAvatar";
import {
  actualTripComplete,
  deleteTrip,
  fetchTripTimeline,
  getTripDetails,
  getTripLogs,
  setActiveTab,
  setSelectedTripId,
  setViewAllTrips,
  tripLists,
  updateTripDetailsLiveData,
  updateTripLiveData,
} from "../../store/Slices/TripSlice";
import {
  convertDateToInputFormat,
  formatLocalDateTime,
  formatUtcDateTimeForApi,
  formatUtcDateTimeForDisplay,
  formatUtcDateTimeForInput,
  hasDateTimePassed,
  hasPermission
} from "../../utils/helper";
import { AppMainModules, TripPermission, TripSubModules } from "../../utils/app-constant";
import { images, imagesAlt } from "../../utils/images";
import { useTripSocket } from "../../utils/SocketTripContext";
import SpinnerLoader from "../../utils/SpinnerLoader";
import {
  acriveHistoryTripIcon,
  activeLiveTripIcon,
  addTripIcon,
  historyTripIcon,
  liveTripIcon,
  plusIcon,
  speedIcon,
  tripMenuDotsIcon
} from "../../utils/svgs";
import { errorNotify, successNotify } from "../../utils/toast";

const getTripCustomerName = (trip = {}) =>
  String(trip?.customer?.name || trip?.customer_name || "").trim();

const getTripDriverName = (trip = {}) =>
  String(trip?.primary_driver?.username || trip?.primary_driver?.name || "").trim();

const TRIP_LIST_LIMIT = 10;

const TripLists = ({ hideTabs = false, onOpenCreateTrip, onMobileCardSelect }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const [activeTab, setActiveTab] = useState("live");
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const { tripSocket } = useTripSocket();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [tripToDelete, setTripToDelete] = useState(null);
  const [activeDeleteId, setActiveDeleteId] = useState(null);
  const [tripCompleteModalOpen, setTripCompleteModalOpen] = useState(false);
  const [tripToComplete, setTripToComplete] = useState(null);
  const [tripCompleteLoading, setTripCompleteLoading] = useState(false);
  const canDelete = hasPermission({
    permissionSlug: TripPermission.Delete,
    menuSlug: TripSubModules.Trips,
    moduleSlug: AppMainModules.tripManagement,
  });

  const handleDeleteTrip = async () => {
    if (!canDelete) {
      errorNotify("You do not have permission to delete trips.");
      return;
    }

    if (tripToDelete) {
      await dispatch(deleteTrip(tripToDelete));
      setIsDeleteModalOpen(false);
      setTripToDelete(null);
      successNotify("Trip Deleted Successfully")
      dispatch(tripLists({ status: "all", activeTab: activeTab, page: page, limit: TRIP_LIST_LIMIT, search_text: debouncedSearchText }));
    }
  };

  const openDeleteModal = (id) => {
    if (!canDelete) return;
    setTripToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const { loading, tripListsData, selectedTripId, tripDetailsData, viewAllTrips, currentPage, totalPages, activeTab } =
    useSelector((state) => state.trips);

  const parseNumberOrZero = (value) => {
    if (value === "" || value === null || value === undefined) return 0;
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? 0 : numericValue;
  };

  const getTripCompletionInitialValues = (trip = {}) => {
    const rawTrip = trip?.raw || trip;
    const rawStops = rawTrip?.trip_stops || rawTrip?.stops || [];
    const stopsArray = Array.isArray(rawStops) ? rawStops : [];
    const totalQty = Number(
      rawTrip?.total_load ??
      rawTrip?.total_quantity ??
      rawTrip?.trip_load_sum?.total_load ??
      rawTrip?.quantity ??
      (stopsArray.length > 0
        ? stopsArray.filter((s) => s.stop_type === "load").reduce((sum, s) => sum + (Number(s.quantity) || 0), 0) ||
          stopsArray.reduce((sum, s) => sum + (Number(s.quantity) || 0), 0)
        : 0)
    ) || 0;

    return {
      startOdometer: rawTrip?.start_odm ?? "",
      endOdometer: rawTrip?.end_odm ?? "",
      closing_fuel: rawTrip?.closing_fuel ?? "",
      shortage_qty: "",
      relief_qty: "",
      delivered_qty: "",
      totalQuantity: totalQty,
      trip_stops: stopsArray,
      startDateTime: formatUtcDateTimeForInput(rawTrip?.start_at),
      actual_end_at: formatUtcDateTimeForInput(rawTrip?.actual_end_at || rawTrip?.end_at),
      tripId: rawTrip?.trip_id || (rawTrip?.id ? `TR-${rawTrip.id}` : ""),
      vehicleReg: rawTrip?.vehicle?.registration_number || rawTrip?.registrationNumber || "",
      customerName: rawTrip?.customer?.name || rawTrip?.customer || "",
    };
  };

  const openTripCompleteModal = (trip) => {
    const startDateTime = formatUtcDateTimeForInput(trip.start_at);

    if (!startDateTime) {
      errorNotify("Start date & time is required before completing the trip.");
      return;
    }

    if (!hasDateTimePassed(startDateTime)) {
      errorNotify(`Trip can be completed after ${formatLocalDateTime(startDateTime)}.`);
      return;
    }

    setTripToComplete(trip);
    setTripCompleteModalOpen(true);
  };

  const closeTripCompleteModal = () => {
    if (tripCompleteLoading) return;
    setTripCompleteModalOpen(false);
    setTripToComplete(null);
  };

  const handleTripCompleteConfirm = async (values) => {
    if (!tripToComplete?.id) return;

    setTripCompleteLoading(true);

    try {
      await dispatch(
        actualTripComplete({
          trip_id: Number(tripToComplete.id),
          end_odm: parseNumberOrZero(values.end_odm),
          closing_fuel: parseNumberOrZero(values.closing_fuel),
          shortage_qty: parseNumberOrZero(values.shortage_qty),
          relief_qty: parseNumberOrZero(values.relief_qty),
          delivered_qty: parseNumberOrZero(values.delivered_qty),
          actual_end_at: formatUtcDateTimeForApi(values.actual_end_at),
        })
      ).unwrap();

      await dispatch(tripLists({ status: "all", activeTab: activeTab, page: page, limit: TRIP_LIST_LIMIT, search_text: debouncedSearchText })).unwrap();

      if (selectedTripId === tripToComplete.id) {
        dispatch(getTripDetails(tripToComplete.id));
        dispatch(fetchTripTimeline(tripToComplete.id));
      }

      successNotify("Trip completed successfully.");
      setTripCompleteModalOpen(false);
      setTripToComplete(null);
    } catch (error) {
      errorNotify(error);
    } finally {
      setTripCompleteLoading(false);
    }
  };


  const timelineHitRef = useRef({});

  const hitTimeline = (tripId, source = "") => {
    if (!tripId) return;

    const now = Date.now();
    const last = timelineHitRef.current[tripId] || 0;

    if (now - last < 300) return;
    timelineHitRef.current[tripId] = now;

    dispatch(fetchTripTimeline(Number(tripId)));
  };

  const handleVehicleLocation = (data, itemId) => {
    dispatch(updateTripDetailsLiveData(data));

  };

  const newLogs = (data) => {

    if (!selectedTripId) return;

    const tid = data?.trip_id ?? data?.tripId ?? selectedTripId;
    if (Number(tid) !== Number(selectedTripId)) return;

    hitTimeline(selectedTripId, "new-logs");
  };

  const handler = (message) => {
    dispatch(updateTripLiveData(message));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setDebouncedSearchText(searchText);
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    dispatch(tripLists({ status: "all", activeTab: activeTab, page: page, limit: TRIP_LIST_LIMIT, search_text: debouncedSearchText }));
  }, [dispatch, activeTab, page, debouncedSearchText]);

  const handleTabChange = (tab) => {
    dispatch(setActiveTab(tab));
    setPage(1);
    if (tab === "history") {
      dispatch(setViewAllTrips(false));
    }
  };

  useEffect(() => {
    if (!tripSocket || !Array.isArray(tripListsData) || !tripListsData.length) return;

    const eventListeners = tripListsData.map((item) => {
      const tripId = item?.id;
      const vehicleId = item?.vehicle?.id || item?.vehicle_id;
      const imeino = item?.vehicle?.imeino || null;
      const distanceEvent = tripId ? `trip-distance-${tripId}` : null;
      const primaryLocEvent = vehicleId ? `vehicle-location-${vehicleId}` : null;
      const fallbackLocEvent = imeino && String(imeino) !== String(vehicleId) ? `vehicle-location-${imeino}` : null;
      const tripNewLogEvent = tripId ? `new-logs-${tripId}` : null;
      const tripViolationEvent = tripId ? `trip-violation-${tripId}` : null;

      const distanceHandler = (message) => {
        handler(message, tripId);
      };
      const vehicleLocationHandler = (data) => {
        handleVehicleLocation(data, data?.id);
      };
      const tripNewLogHandler = (data) => {
        newLogs(data, data?.id);
      };
      const tripViolationHandler = (data) => {
        hitTimeline(tripId, "trip-violation");
      };

      if (distanceEvent) {
        tripSocket.on(distanceEvent, distanceHandler);
      }
      if (primaryLocEvent) {
        tripSocket.on(primaryLocEvent, vehicleLocationHandler);
      }
      if (fallbackLocEvent) {
        tripSocket.on(fallbackLocEvent, vehicleLocationHandler);
      }
      if (tripNewLogEvent) {
        tripSocket.on(tripNewLogEvent, tripNewLogHandler);
      }
      if (tripViolationEvent) {
        tripSocket.on(tripViolationEvent, tripViolationHandler);
      }

      return {
        distanceEvent,
        primaryLocEvent,
        fallbackLocEvent,
        tripNewLogEvent,
        tripViolationEvent,
        distanceHandler,
        vehicleLocationHandler,
        tripNewLogHandler,
        tripViolationHandler,
      };
    });

    return () => {
      eventListeners.forEach(
        ({
          distanceEvent,
          primaryLocEvent,
          fallbackLocEvent,
          tripNewLogEvent,
          tripViolationEvent,
          distanceHandler,
          vehicleLocationHandler,
          tripNewLogHandler,
          tripViolationHandler,
        }) => {
          if (distanceEvent) tripSocket.off(distanceEvent, distanceHandler);
          if (primaryLocEvent) tripSocket.off(primaryLocEvent, vehicleLocationHandler);
          if (fallbackLocEvent) tripSocket.off(fallbackLocEvent, vehicleLocationHandler);
          if (tripNewLogEvent) tripSocket.off(tripNewLogEvent, tripNewLogHandler);
          if (tripViolationEvent) tripSocket.off(tripViolationEvent, tripViolationHandler);
        }
      );
    };
  }, [tripSocket, tripListsData, selectedTripId]);
  // Auto select first trip only when viewAll is off
  useEffect(() => {
    if (window.innerWidth <= 768) return;
    if (viewAllTrips) return;

    const isSelectedInList = tripListsData?.find((t) => t.id === selectedTripId);

    if (tripListsData?.length && (!selectedTripId || !isSelectedInList)) {
      const firstId = tripListsData[0]?.id;
      if (firstId) {
        dispatch(setSelectedTripId(firstId));
        dispatch(fetchTripTimeline(firstId));
        dispatch(getTripDetails(firstId));
        dispatch(getTripLogs(firstId));
      }
    }
  }, [tripListsData, selectedTripId, viewAllTrips, dispatch]);

  // Subscribe vehicle-location + new-logs + trip-violation for current selected trip
  useEffect(() => {
    if (!tripSocket) return;

    // vehicle-location: vehicle_id (Primary) and IMEI (Fallback)
    const vehicleId = tripDetailsData?.vehicle?.id || tripDetailsData?.vehicle_id;
    const imei = tripDetailsData?.vehicle?.imeino;
    const primaryLocEvent = vehicleId ? `vehicle-location-${vehicleId}` : null;
    const fallbackLocEvent = imei && String(imei) !== String(vehicleId) ? `vehicle-location-${imei}` : null;

    // new-logs depends on selectedTripId
    const tripNewLogEvent = selectedTripId ? `new-logs-${selectedTripId}` : null;

    // trip-violation depends on selectedTripId
    const tripViolationEvent = selectedTripId ? `trip-violation-${selectedTripId}` : null;

    const handleTripViolation = (data) => {
      console.log("Trip violation socket received for selected trip:", data);
      hitTimeline(selectedTripId, "trip-violation");
    };

    if (primaryLocEvent) {
      tripSocket.on(primaryLocEvent, handleVehicleLocation);
    }
    if (fallbackLocEvent) {
      tripSocket.on(fallbackLocEvent, handleVehicleLocation);
    }

    if (tripNewLogEvent) {
      tripSocket.on(tripNewLogEvent, newLogs);
    }

    if (tripViolationEvent) {
      tripSocket.on(tripViolationEvent, handleTripViolation);
    }

    return () => {
      if (primaryLocEvent) tripSocket.off(primaryLocEvent, handleVehicleLocation);
      if (fallbackLocEvent) tripSocket.off(fallbackLocEvent, handleVehicleLocation);
      if (tripNewLogEvent) tripSocket.off(tripNewLogEvent, newLogs);
      if (tripViolationEvent) tripSocket.off(tripViolationEvent, handleTripViolation);
    };
  }, [tripSocket, tripDetailsData, selectedTripId]);

  const tripsTabs = [
    {
      key: "live",
      label: "Live Trips",
      icon: activeTab === "live" ? activeLiveTripIcon : liveTripIcon,
    },
    {
      key: "history",
      label: "History",
      icon: activeTab === "history" ? acriveHistoryTripIcon : historyTripIcon,
    },
  ];

  return (
    <div>
      {!hideTabs && (
        <CustomTabs
          tabs={tripsTabs}
          activeTab={activeTab}
          setActiveTab={handleTabChange}
        />
      )}

      <Search value={searchText} onChange={(e) => setSearchText(e.target.value)} />

      {
        loading ? <SpinnerLoader /> :
          tripListsData?.length === 0 ? (
            <div className="add_trip_suggestion_div">
              {addTripIcon}
              <h4>There is nothing here...</h4>
              <p>
                There is nothing to view right now, please create a new trip to get
                started.
              </p>
              <Button
                iconButton={true}
                type="button"
                iconBtnText="Create New Trip"
                isShowCancel={false}
                icon={plusIcon}
                shouldRow={false}
                isShowSubmitBtn={false}
                onClick={() => {
                  if (onOpenCreateTrip) {
                    onOpenCreateTrip();
                  } else {
                    navigate("/trips?create=true");
                  }
                }}
              />
            </div>
          ) : (
            <>
              {tripListsData?.map((trip, index) => {
                const isActive = !viewAllTrips && selectedTripId === trip.id;
                const driverName = getTripDriverName(trip);
                const customerName = getTripCustomerName(trip);

                return (
                  <div
                    className={`trip_list_main_div ${isActive ? "active" : ""}`}
                    key={index}
                    onClick={() => {
                      if (viewAllTrips) {
                        dispatch(setViewAllTrips(false));
                      }
                      if (!viewAllTrips && selectedTripId === trip.id) return;

                      dispatch(setSelectedTripId(trip?.id));
                      dispatch(fetchTripTimeline(trip?.id));
                      dispatch(getTripDetails(trip?.id));
                      dispatch(getTripLogs(trip?.id));

                      if (window.innerWidth <= 768 && onMobileCardSelect) {
                        onMobileCardSelect();
                      }
                    }}
                  >
                    <div className="trip_list_header">
                      <h5>
                        <span
                          style={{ color: "#1447b1", fontWeight: "600" }}
                        >
                          {trip?.trip_id}
                        </span>
                      </h5>
                      <div style={{ gap: "6px" }}>
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: 600,
                            padding: "4px 8px",
                            borderRadius: "4px",

                            background:
                              trip.status === "new"
                                ? "#f1f5f9"
                                : trip.status === "in_transit"
                                  ? "#e0f2fe"
                                  : trip.status === "driver_completed"
                                    ? "#fef3c7"
                                    : trip.status === "completed"
                                      ? "#dcfce7"
                                      : trip.status === "closed"
                                        ? "#e5e7eb"
                                        : "#f1f5f9",

                            color:
                              trip.status === "new"
                                ? "#475569"
                                : trip.status === "in_transit"
                                  ? "#0369a1"
                                  : trip.status === "driver_completed"
                                    ? "#b45309"
                                    : trip.status === "completed"
                                      ? "#15803d"
                                      : trip.status === "closed"
                                        ? "#374151"
                                        : "#475569",

                            textTransform: "capitalize",
                            fontFamily: "'Roboto', sans-serif",
                            display: "inline-block",
                            textAlign: "center",
                          }}
                        >
                          {trip.status ? trip.status.replace("_", " ") : "—"}
                        </span>

                        {canDelete && (
                          <b style={{ cursor: "pointer", position: "relative", zIndex: "5" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDeleteId(activeDeleteId === trip.id ? null : trip.id);
                            }}>
                            {tripMenuDotsIcon}
                            {
                              activeDeleteId === trip.id && (
                                <div className="delete_trip_popover" onClick={(e) => {
                                  e.stopPropagation();
                                  openDeleteModal(trip.id);
                                  setActiveDeleteId(null);
                                }}>
                                  Delete Trip
                                </div>
                              )
                            }
                          </b>
                        )}
                      </div>
                    </div>
                    <div className="trip_list_body">
                      <div className="trip_loc_view">
                        <p>
                          Last updated:{" "}
                          {trip.last_updated_at
                            ? new Date(trip.last_updated_at).toLocaleString()
                            : "N/A"}
                        </p>

                        <div className="trip-route">
                          <div className="route-line">
                            <span className="route-dot start"></span>

                            <Tooltip
                              color="white"
                              overlayInnerStyle={{ color: 'black', width: '300px', padding: '15px' }}
                              title={
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#525c76', fontSize: '12px' }}>Actual Start Date & Time</span>
                                    <span style={{ color: '#525c76', fontWeight: '500', fontSize: '12px' }}>{formatUtcDateTimeForDisplay(trip.actual_start_at)}</span>
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#525c76', fontSize: '12px' }}>Departure Date & Time</span>
                                    <span style={{ color: '#525c76', fontWeight: '500', fontSize: '12px' }}>{formatUtcDateTimeForDisplay(trip.start_at)}</span>
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#525c76', fontSize: '12px' }}>Expected Date & Time</span>
                                    <span style={{ color: '#525c76', fontWeight: '500', fontSize: '12px' }}>{formatUtcDateTimeForDisplay(trip.expected_end_at)}</span>
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#525c76', fontSize: '12px' }}>Actual End Date & Time</span>
                                    <span style={{ color: '#525c76', fontWeight: '500', fontSize: '12px' }}>{formatUtcDateTimeForDisplay(trip.actual_end_at || trip.end_at)}</span>
                                  </div>
                                </div>
                              }
                            >
                              <div className="route-progress">
                                <div
                                  className="filled"
                                  style={{
                                    width: `${convertDateToInputFormat(
                                      trip.distance,
                                      trip.covered_distance
                                    )}%`,
                                  }}>
                                </div>
                              </div>
                            </Tooltip>

                            <span className="route-dot end"></span>
                          </div>

                          <div className="route-labels">
                            <p>{trip.dispatch_point}</p>
                            <p className="text-end">{trip.decanting_point}</p>
                          </div>
                        </div>
                      </div>

                      <div className="trip_speed_loc">
                        <p>Current Speed & Location</p>
                        <h6>
                          {speedIcon} {trip?.trip_current_detail?.speed || 0}{" "}
                          <sub>km/hr</sub>
                        </h6>
                        <span>
                          {trip?.trip_current_detail?.lat || 0},{" "}
                          {trip?.trip_current_detail?.lng || 0}
                        </span>
                      </div>
                    </div>

                    <div className="trip_list_footer">
                      <div className="vehicle_details">
                        <img src={images.vehcileImg} alt={imagesAlt.vehcileImg} />
                        <p>
                          <span>{trip.vehicle?.registration_number}</span> Vehicle
                        </p>
                      </div>
                      <div className="driver_details">
                        <UserAvatar username={driverName} image={trip.primary_driver?.image} />
                        <p> <span>{driverName || "-"}</span> Vehicle Driver</p>
                      </div>
                      {customerName && (
                        <div className="trip_list_customer_row">
                          <div className="customer_details">
                            <UserAvatar username={customerName} />
                            <p> <span>{customerName}</span> Customer</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="text-center p-1">
                      <img
                        src={images.allCarFunctionsImg}
                        alt={imagesAlt.allCarFunctionsImg}
                      />
                    </div>
                  </div>
                );
              })}
              {activeTab === "history" && currentPage < totalPages && (
                <div className="d-flex justify-content-center my-3">
                  <Button
                    submitText={loading ? "Loading..." : "Load More"}
                    type="button"
                    shouldRow={false}
                    isShowSubmitBtn={true}
                    submitBtn={() => setPage((prev) => prev + 1)}
                    disabled={loading}
                    isShowCancel={false}
                  />
                </div>
              )}
            </>
          )}
      <DeleteModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onLogout={handleDeleteTrip}
        loading={loading}
        topHeading={"Delete Trip"}
        description={"Are you sure you want to delete this trip?"}
        subDescription={"Deleting this trip will remove it from your account."}
      />

      <TripCompleteModal
        open={tripCompleteModalOpen}
        onClose={closeTripCompleteModal}
        onConfirm={handleTripCompleteConfirm}
        loading={tripCompleteLoading}
        initialValues={getTripCompletionInitialValues(tripToComplete)}
      />
    </div>
  );
};

export default TripLists;
